/**
 * PlansPage - "Meus Planos" subscription management page.
 *
 * Route: /meus-planos (protected, with AppLayout sidebar)
 *
 * Features:
 * - Period tabs (Monthly/Yearly)
 * - Plan cards grid with current plan highlight
 * - Payment modal for new subscriptions
 * - Plan change flow (upgrade/downgrade)
 * - Responsive layout
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from '@/contexts/LanguageContext';
import { Loader2, CreditCard, Receipt, AlertTriangle } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import PeriodTabs from '@/components/PeriodTabs';
import PlanCard from '@/components/PlanCard';
import PaymentModal from '@/components/PaymentModal';
import CancelSurveyModal from '@/components/CancelSurveyModal';
import type { PaymentFormData } from '@/components/PaymentModal';
import { getPlans, checkout, changePlan, getInvoices, cancelSubscription } from '@/services/plansService';
import { buildCheckoutUrlWithUtms } from '@/hooks/useUtmCapture';
import type {
  Plan,
  CurrentSubscription,
  BillingPeriod,
  SubscriptionStatus,
  Invoice,
  InvoiceStatus,
} from '@/types';
import { blockInSupportMode } from '@/lib/impersonation';

// ============================================================================
// Renewal card helpers
// ============================================================================

const STATUS_STYLES: Record<SubscriptionStatus, string> = {
  ACTIVE: 'bg-green-500/10 text-green-600 border-green-500/20',
  TRIALING: 'bg-blue-500/10 text-blue-600 border-blue-500/20',
  PENDING: 'bg-yellow-500/10 text-yellow-600 border-yellow-500/20',
  PAST_DUE: 'bg-orange-500/10 text-orange-600 border-orange-500/20',
  CANCELED: 'bg-red-500/10 text-red-600 border-red-500/20',
};

const STATUS_KEYS: Record<SubscriptionStatus, string> = {
  ACTIVE: 'plans.renewal.active',
  TRIALING: 'plans.renewal.trialing',
  PENDING: 'plans.renewal.pending',
  PAST_DUE: 'plans.renewal.pastDue',
  CANCELED: 'plans.renewal.canceled',
};

function StatusBadge({ status, t }: { status: SubscriptionStatus; t: (key: string) => string }) {
  return (
    <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-medium border ${STATUS_STYLES[status] || ''}`}>
      {t(STATUS_KEYS[status] || '')}
    </span>
  );
}

function RenewalRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between px-5 py-3">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="text-sm font-medium text-foreground">{value}</span>
    </div>
  );
}

// ============================================================================
// Invoice table helpers
// ============================================================================

const INVOICE_STATUS_STYLES: Record<InvoiceStatus, string> = {
  PAID: 'bg-green-500/10 text-green-600 border-green-500/20',
  PENDING: 'bg-yellow-500/10 text-yellow-600 border-yellow-500/20',
  FAILED: 'bg-red-500/10 text-red-600 border-red-500/20',
};

const INVOICE_STATUS_KEYS: Record<InvoiceStatus, string> = {
  PAID: 'plans.invoices.paid',
  PENDING: 'plans.invoices.pending',
  FAILED: 'plans.invoices.failed',
};

function InvoiceStatusBadge({ status, t }: { status: InvoiceStatus; t: (key: string) => string }) {
  return (
    <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-medium border ${INVOICE_STATUS_STYLES[status] || ''}`}>
      {t(INVOICE_STATUS_KEYS[status] || '')}
    </span>
  );
}

// ============================================================================
// PlansPage
// ============================================================================

const PlansPage: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const autoOpenHandled = useRef(false);

  // Data state
  const [plans, setPlans] = useState<Plan[]>([]);
  const [currentSubscription, setCurrentSubscription] = useState<CurrentSubscription | null>(null);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // UI state
  const [period, setPeriod] = useState<BillingPeriod>('MONTHLY');
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Cancel state
  const [showSurveyModal, setShowSurveyModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelReason, setCancelReason] = useState<string | null>(null);
  const [cancelFeedback, setCancelFeedback] = useState<string | undefined>(undefined);

  // Load plans
  const loadPlans = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [plansData, invoicesData] = await Promise.all([
        getPlans(),
        getInvoices().catch(() => [] as Invoice[]),
      ]);
      setPlans(plansData.plans);
      setCurrentSubscription(plansData.currentSubscription);
      setInvoices(invoicesData);
    } catch (err: any) {
      setError(err.message || t('plans.error'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadPlans();
  }, [loadPlans]);

  // Auto-select the first available billing period based on plan prices
  useEffect(() => {
    if (plans.length === 0) return;
    const hasMonthly = plans.some((p) => p.priceMonthly > 0);
    const hasQuarterly = plans.some((p) => p.priceQuarterly > 0);
    const hasYearly = plans.some((p) => p.priceYearly > 0);

    if (period === 'MONTHLY' && !hasMonthly) {
      if (hasQuarterly) setPeriod('QUARTERLY');
      else if (hasYearly) setPeriod('YEARLY');
    }
  }, [plans, period]);

  // Auto-open PaymentModal when planId is passed via URL (from PaywallModal)
  useEffect(() => {
    if (plans.length === 0 || autoOpenHandled.current) return;
    const planId = searchParams.get('planId');
    if (!planId) return;

    const plan = plans.find(p => p.planId === planId);
    if (plan && !blockInSupportMode()) {
      if (plan.checkoutUrl) {
        window.open(buildCheckoutUrlWithUtms(plan.checkoutUrl), '_blank');
      } else {
        // Select best available period (yearly first for best value)
        if (plan.priceYearly > 0) setPeriod('YEARLY');
        else if (plan.priceQuarterly > 0) setPeriod('QUARTERLY');
        else if (plan.priceMonthly > 0) setPeriod('MONTHLY');

        setSelectedPlan(plan);
        setShowPaymentModal(true);
      }
    }

    // Clean up URL param to avoid reopening on navigation
    autoOpenHandled.current = true;
    searchParams.delete('planId');
    setSearchParams(searchParams, { replace: true });
  }, [plans, searchParams, setSearchParams]);

  // Handle subscribe (new subscription)
  const handleSubscribe = (plan: Plan) => {
    if (blockInSupportMode()) return;
    if (plan.checkoutUrl) {
      window.open(buildCheckoutUrlWithUtms(plan.checkoutUrl), '_blank');
      return;
    }
    setSelectedPlan(plan);
    setShowPaymentModal(true);
    setSuccessMessage(null);
  };

  // Handle plan change (upgrade/downgrade)
  const handleChangePlan = async (plan: Plan) => {
    if (blockInSupportMode()) return;
    setSuccessMessage(null);
    try {
      const result = await changePlan({
        newPlanId: plan.planId,
        period,
      });
      setSuccessMessage(result.message);
      await loadPlans();
    } catch (err: any) {
      setError(err.message || t('plans.error'));
    }
  };

  // Handle payment confirmation
  const handlePaymentConfirm = async (payment: PaymentFormData) => {
    if (!selectedPlan || blockInSupportMode()) return;
    setCheckoutLoading(true);
    try {
      const result = await checkout({
        planId: selectedPlan.planId,
        period,
        payment,
      });
      setShowPaymentModal(false);
      // Fire Meta Pixel Purchase event on approved payment
      if (result.status === 'ACTIVE' && typeof fbq === 'function') {
        const value =
          period === 'MONTHLY' ? selectedPlan.priceMonthly
            : period === 'QUARTERLY' ? selectedPlan.priceQuarterly
            : selectedPlan.priceYearly;
        fbq('track', 'Purchase', { value, currency: 'BRL' });
      }
      setSelectedPlan(null);
      setSuccessMessage(result.message);
      await loadPlans();
    } catch (err: any) {
      throw err; // Re-throw so PaymentModal shows the error
    } finally {
      setCheckoutLoading(false);
    }
  };

  // Close modal
  const handleCloseModal = () => {
    if (!checkoutLoading) {
      setShowPaymentModal(false);
      setSelectedPlan(null);
    }
  };

  // Handle survey submission → open confirmation modal
  const handleSurveySubmit = (reason: string, feedback?: string) => {
    setCancelReason(reason);
    setCancelFeedback(feedback);
    setShowSurveyModal(false);
    setShowCancelModal(true);
  };

  // Handle cancel subscription
  const handleCancelSubscription = async () => {
    if (!cancelReason || blockInSupportMode()) return;
    setCancelLoading(true);
    try {
      const result = await cancelSubscription({ reason: cancelReason, feedback: cancelFeedback });
      setShowCancelModal(false);

      if (result.contactSupport) {
        setSuccessMessage(t('plans.cancel.successContactSupport'));
      } else if (result.refunded) {
        setSuccessMessage(t('plans.cancel.successRefunded'));
      } else if (result.accessUntil) {
        const date = new Date(result.accessUntil).toLocaleDateString('pt-BR');
        setSuccessMessage(t('plans.cancel.successNoRefund', { date }));
      } else {
        setSuccessMessage(result.message);
      }

      await loadPlans();
    } catch (err: any) {
      setError(err.message || t('plans.error'));
      setShowCancelModal(false);
    } finally {
      setCancelLoading(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center h-full min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <h1 className="text-2xl font-bold text-foreground">
          {t('plans.title')}
        </h1>
        <PeriodTabs
          period={period}
          onPeriodChange={setPeriod}
          availablePeriods={[
            ...(plans.some((p) => p.priceMonthly > 0) ? ['MONTHLY' as const] : []),
            ...(plans.some((p) => p.priceQuarterly > 0) ? ['QUARTERLY' as const] : []),
            ...(plans.some((p) => p.priceYearly > 0) ? ['YEARLY' as const] : []),
          ]}
        />
      </div>

      {/* Success message */}
      {successMessage && (
        <div className="p-4 rounded-lg bg-green-500/10 text-green-600 border border-green-500/20 mb-6">
          {successMessage}
        </div>
      )}

      {/* Error message */}
      {error && (
        <div className="p-4 rounded-lg bg-destructive/10 text-destructive border border-destructive/20 mb-6">
          {error}
        </div>
      )}

      {/* Plans grid */}
      {plans.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground">
          {t('plans.noPlans')}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-4xl mx-auto">
          {plans.map((plan) => (
            <PlanCard
              key={plan.planId}
              plan={plan}
              period={period}
              currentSubscription={currentSubscription}
              onSubscribe={handleSubscribe}
              onChangePlan={handleChangePlan}
            />
          ))}
        </div>
      )}

      {/* Renewal card */}
      {currentSubscription && (
        <Card className="mt-8 overflow-hidden">
          <div className="flex items-center gap-3 p-5 border-b border-border">
            <CreditCard className="h-5 w-5 text-primary" />
            <h2 className="text-base font-semibold text-foreground">
              {t('plans.renewal.title')}
            </h2>
          </div>
          <div className="divide-y divide-border">
            <RenewalRow label={t('plans.renewal.plan')} value={currentSubscription.planName} />
            <RenewalRow
              label={t('plans.renewal.period')}
              value={
                currentSubscription.period === 'MONTHLY' ? t('plans.monthly')
                  : currentSubscription.period === 'QUARTERLY' ? t('plans.quarterly')
                  : t('plans.yearly')
              }
            />
            <RenewalRow
              label={t('plans.renewal.value')}
              value={(() => {
                const matchedPlan = plans.find((p) => p.planId === currentSubscription.planId);
                if (!matchedPlan) return '—';
                const price = currentSubscription.period === 'MONTHLY'
                  ? matchedPlan.priceMonthly
                  : currentSubscription.period === 'QUARTERLY'
                    ? matchedPlan.priceQuarterly
                    : matchedPlan.priceYearly;
                return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(price);
              })()}
            />
            <RenewalRow
              label={t('plans.renewal.status')}
              value={<StatusBadge status={currentSubscription.status} t={t} />}
            />
            {currentSubscription.renewAt && (
              <RenewalRow
                label={t('plans.renewal.nextRenewal')}
                value={new Date(currentSubscription.renewAt).toLocaleDateString('pt-BR')}
              />
            )}
          </div>
          {/* Cancel button */}
          {(currentSubscription.status === 'ACTIVE' || currentSubscription.status === 'TRIALING') && (
            <div className="px-5 py-4 border-t border-border">
              <Button
                variant="outline"
                size="sm"
                onClick={() => { if (!blockInSupportMode()) setShowSurveyModal(true); }}
                className="text-destructive border-destructive/30 hover:bg-destructive/10 hover:text-destructive"
              >
                {t('plans.cancel.cancelButton')}
              </Button>
            </div>
          )}
        </Card>
      )}

      {/* Cancel Survey Modal */}
      <CancelSurveyModal
        open={showSurveyModal}
        onClose={() => setShowSurveyModal(false)}
        onSubmit={handleSurveySubmit}
      />

      {/* Cancel Confirmation Modal */}
      {showCancelModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
          onClick={() => { if (!cancelLoading) { setShowCancelModal(false); setCancelReason(null); setCancelFeedback(undefined); } }}
        >
          <div
            className="bg-background rounded-xl shadow-xl max-w-md w-full mx-4 p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 rounded-full bg-destructive/10">
                <AlertTriangle className="h-5 w-5 text-destructive" />
              </div>
              <h3 className="text-lg font-semibold text-foreground">
                {t('plans.cancel.confirmTitle')}
              </h3>
            </div>
            <p className="text-sm text-muted-foreground mb-6">
              {t('plans.cancel.confirmMessage')}
            </p>
            <div className="flex gap-3 justify-end">
              <Button
                variant="outline"
                onClick={() => { setShowCancelModal(false); setCancelReason(null); setCancelFeedback(undefined); }}
                disabled={cancelLoading}
              >
                {t('plans.cancel.keepButton')}
              </Button>
              <Button
                variant="destructive"
                onClick={handleCancelSubscription}
                disabled={cancelLoading}
              >
                {cancelLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : null}
                {t('plans.cancel.confirmButton')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Invoices table */}
      <Card className="mt-6 overflow-hidden">
        <div className="flex items-center gap-3 p-5 border-b border-border">
          <Receipt className="h-5 w-5 text-primary" />
          <h2 className="text-base font-semibold text-foreground">
            {t('plans.invoices.title')}
          </h2>
        </div>
        {invoices.length === 0 ? (
          <div className="p-8 text-center text-sm text-muted-foreground">
            {t('plans.invoices.empty')}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/50">
                  <th className="px-5 py-3 text-left font-medium text-muted-foreground">{t('plans.invoices.date')}</th>
                  <th className="px-5 py-3 text-left font-medium text-muted-foreground">{t('plans.invoices.plan')}</th>
                  <th className="px-5 py-3 text-left font-medium text-muted-foreground">{t('plans.invoices.period')}</th>
                  <th className="px-5 py-3 text-right font-medium text-muted-foreground">{t('plans.invoices.amount')}</th>
                  <th className="px-5 py-3 text-right font-medium text-muted-foreground">{t('plans.invoices.status')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {invoices.map((inv) => (
                  <tr key={inv.id} className="hover:bg-muted/30 transition-colors">
                    <td className="px-5 py-3 text-foreground">
                      {new Date(inv.createdAt).toLocaleDateString('pt-BR')}
                    </td>
                    <td className="px-5 py-3 text-foreground">{inv.planName}</td>
                    <td className="px-5 py-3 text-foreground">
                      {inv.period === 'MONTHLY' ? t('plans.monthly')
                        : inv.period === 'QUARTERLY' ? t('plans.quarterly')
                        : t('plans.yearly')}
                    </td>
                    <td className="px-5 py-3 text-right text-foreground">
                      {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(inv.amount)}
                    </td>
                    <td className="px-5 py-3 text-right">
                      <InvoiceStatusBadge status={inv.status} t={t} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Payment Modal */}
      {selectedPlan && (
        <PaymentModal
          open={showPaymentModal}
          onClose={handleCloseModal}
          onConfirm={handlePaymentConfirm}
          plan={selectedPlan}
          period={period}
          loading={checkoutLoading}
        />
      )}
    </div>
  );
};

export default PlansPage;
